import React, { useContext, useEffect, useState } from 'react';
import { TopHeadlineImage, TopHeadlineImageSmall } from '../../../types/api/ImageType';
import {
  CheckBoxParam,
  Form,
  HiddenParam,
  ImageUploadWithCropping,
  Radio,
  RadioGroup,
  RadioParam,
  RequiredHelpPhrase,
  Row,
  SonForm,
  SubmitButton,
  Switch,
  TextParam,
  useForm,
  VideoFormTemplate,
} from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { MultiStepForm, Step } from '../../../components/molecules/Form/components/MultiStepForm';
import Information, { InformationParams } from './partials/Information';
import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';
import { Namespace } from '../../../translation/namespace';
import dayjs from 'dayjs';
import { BlockType } from '../../../types/api/ElementType';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import ModalContext from '../../../components/organisms/ModalDialog/ModalContext';
import useRedirect from '../../../hooks/useRedirect';
import { formParamsUtils } from '../../../_lib/Form2/utils/formParamsUtils';
import { NestedParam } from '../../../_lib/Form2/types/NestedParam';
import { ImageFileParam } from '../../../_lib/Form2/types/FileUploadParam';
import { VideoWithMetaInfoType } from '../../../_lib/components/Video/VideoWithMetaInfoType';
import { Form2Modifier } from '../../../_lib/Form2/Form';

export type FormType = 'create' | 'update';
export type TopHeadlinesForm = SonForm<
  | InformationParams
  | (
      | CheckBoxParam<'largeIfFirst'>
      | RadioParam<'teaserMediaType', 'image' | 'video'>
      | NestedParam<
          'imageuploadparams',
          'imageupload',
          | ImageFileParam<'image'>
          | HiddenParam<'width'>
          | HiddenParam<'height'>
          | HiddenParam<'y'>
          | HiddenParam<'x'>
          | TextParam<'alt'>
          | TextParam<'copyright'>
          | TextParam<'caption'>
        >
      | NestedParam<
          'videoparams',
          'video',
          TextParam<'title'> | TextParam<'source'> | TextParam<'copyright'> | TextParam<'captionsFile'>
        >
    )
>;

type TopHeadlinesFormProps = {
  form: TopHeadlinesForm;
  type: FormType;
  image?: TopHeadlineImage | TopHeadlineImageSmall;
  video?: VideoWithMetaInfoType;
};

export function TopHeadlinesForm({ form, type }: TopHeadlinesFormProps) {
  const { t } = useSonTranslation('TopHeadlinesForm');

  const { portalConfiguration } = useAppContext();
  const { elementType } = useBlockContext();
  const { preventModalClose, closePreventedMessage } = useContext(ModalContext);
  const [startRedirect] = useRedirect();

  const id = type === 'create' ? 'top-headline-create-form' : 'top-headline-update-form';
  const { formProps, fetchState, params } = useForm({
    form,
    onSuccess: value => {
      if (value.data.url) {
        startRedirect(value.data.url);
      }
    },
  });

  const daterange = params.getByName('daterange');
  const imageUploadParams = params.getByName('imageuploadparams');
  const videoParams = formParamsUtils(params.getByName('videoparams'));
  const largeIfFirstParam = params.getByName('largeIfFirst');
  const teaserMediaTypeParams = params.getAllByName('teaserMediaType');

  const [currentTeaserMediaType, setCurrentTeaserMediaType] = useState<'image' | 'video'>(
    teaserMediaTypeParams.find(mediaType => mediaType.checked)?.value || 'image'
  );

  useEffect(() => {
    if (fetchState.type === 'loading') {
      preventModalClose(true);
    }

    if (fetchState.type === 'error') {
      preventModalClose(false);
    }
  }, [startRedirect, preventModalClose, fetchState]);

  const getLinkedForwardMessage = () => {
    const endDateOfTopHeadline = formParamsUtils(daterange).getByName('endDate').value;
    if (endDateOfTopHeadline) {
      const todayFormatted = dayjs().format('YYYY-MM-DD');

      if (elementType === BlockType.pagemanagement && todayFormatted > endDateOfTopHeadline) {
        return `${Namespace.TopHeadlinesForm}:status.create.successForwardArchive`;
      }
      if (elementType === BlockType.topheadlinesarchive && todayFormatted < endDateOfTopHeadline) {
        return `${Namespace.TopHeadlinesForm}:status.create.successForwardTopHeadlines`;
      }
      if (elementType === BlockType.topheadlines && todayFormatted > endDateOfTopHeadline) {
        return `${Namespace.TopHeadlinesForm}:status.create.successForwardArchive`;
      }
    }

    return `${Namespace.TopHeadlinesForm}:status.create.successForwardUpdate`;
  };

  const getSubmitButton = (
    <SubmitButton
      buttonLabel={t(`saveButton.${type}`)}
      closePreventedMessage={closePreventedMessage}
      fetchState={fetchState}
      messageFail={translatePartOfStringWithLink(
        `${Namespace.TopHeadlinesForm}:status.create.fail`,
        portalConfiguration.son_portal_support_email && `mailto:${portalConfiguration.son_portal_support_email}`
      )}
      messageSending={t('status.create.sending')}
      messageSuccess={t('status.create.success')}
      messageForward={getLinkedForwardMessage()}
    />
  );

  return (
    <Form {...formProps} id={id} modifier={Form2Modifier.NO_PADDING}>
      <MultiStepForm initialActiveTab="top-headlines-form-informations">
        <Step id="top-headlines-form-informations" buttonLabel={t('steps.informations')}>
          <RequiredHelpPhrase />
          <Information params={params as ReturnType<typeof formParamsUtils<InformationParams>>} />
        </Step>
        <Step id="top-headlines-form-teaserimage" buttonLabel={t('steps.teaserimage')} submitButton={getSubmitButton}>
          <RadioGroup legend={t(`teaserMediaType.fieldSet`)}>
            {teaserMediaTypeParams.map(teaserMediaType => (
              <Radio
                key={teaserMediaType.value}
                label={t(`teaserMediaType.${teaserMediaType.value}.label`)}
                param={teaserMediaType}
                onChange={event => setCurrentTeaserMediaType(event.target.value as 'video' | 'image')}
              />
            ))}
          </RadioGroup>
          {currentTeaserMediaType === 'image' ? <ImageUploadWithCropping param={imageUploadParams} /> : null}
          {currentTeaserMediaType === 'video' ? (
            <VideoFormTemplate
              source={videoParams.getByName('source')}
              captionsFile={videoParams.getByName('captionsFile')}
              copyright={videoParams.getByName('copyright')}
            />
          ) : null}
          <Row>
            <Switch label={t(`largeIfFirst.${currentTeaserMediaType}`)} param={largeIfFirstParam} />
          </Row>
        </Step>
      </MultiStepForm>
    </Form>
  );
}
